import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { Calendar } from '@components/ui/calendar';
import { Button } from '@components/ui/button';
import { SvgConsumer } from '@components/icons/svgConsumer';
import { JOB_DAY_STATUSES, jobDayCalendarStyle } from '../constants';
import { getJobDayKey } from '../helpers';
import { JobDaysNavigationProps } from '../types';
import { JobDaysLegend } from '../legend';

export const JobDaysCalendar = ({ days, activeKey, onSelect }: JobDaysNavigationProps) => {
  const { t } = useTranslation('app', { keyPrefix: 'pages.job.days' });
  const [open, setOpen] = useState(false);

  const activeDay = days.find((el) => el.key === activeKey);
  const keys = useMemo(() => new Set(days.map((el) => el.key)), [days]);
  const modifiers = useMemo(
    () => ({
      ...Object.fromEntries(
        JOB_DAY_STATUSES.map((status) => [
          status,
          days.filter((el) => el.status === status).map((el) => el.day),
        ]),
      ),
      active: activeDay ? [activeDay.day] : [],
    }),
    [days, activeDay],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          disabled={false}
          variant="ghost"
          className="h-[34px] w-[34px] max-[899px]:h-11 max-[899px]:w-11 shrink-0 rounded-lg border border-primary-500 bg-white p-0 text-base text-primary-500 hover:bg-primary-50 hover:text-primary-500"
          aria-label={t('calendar')}>
          <SvgConsumer id="calendar" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <Calendar
          defaultMonth={activeDay?.day}
          modifiers={modifiers}
          modifiersClassNames={jobDayCalendarStyle}
          disabled={(day) => !keys.has(getJobDayKey(day))}
          onDayClick={(day, { disabled }) => {
            if (disabled) return;
            onSelect(getJobDayKey(day));
            setOpen(false);
          }}
        />
        <JobDaysLegend />
      </PopoverContent>
    </Popover>
  );
};
